import ArticleLayout from './components/ArticleLayout';
import { articlesMeta } from '@/mocks/articlesData';

const data = articlesMeta.find((a) => a.slug === 'sugargoo-warehouse-guide')!;

export default function WarehouseGuide() {
  return (
    <ArticleLayout data={data}>
      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Introduction: The Heart of the Sugargoo Experience
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          The <strong>Sugargoo warehouse</strong> is where the magic happens. It is the critical middle step between your order being placed and your package arriving at your door. Understanding how the warehouse operates — from receiving items to inspecting quality to consolidating shipments — gives you the knowledge to shop smarter and avoid common pitfalls.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          This guide explains every stage of the warehouse process in detail, from the moment a seller ships your item to Sugargoo facility to the point where your consolidated package is handed to the international courier. We will cover QC photos, storage rules, return procedures, and the consolidation process that can save you significant shipping costs.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          Whether you are preparing your first order or looking to optimize your warehouse workflow, this comprehensive warehouse guide has you covered.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          What Happens at the Sugargoo Warehouse?
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          The warehouse process follows a standardized sequence designed to protect buyers and ensure quality. Here is what happens behind the scenes:
        </p>
        <div className="space-y-4">
          {[
            {
              step: 'Item Reception',
              desc: 'When a seller ships your purchased item, it travels to the Sugargoo warehouse address. Upon arrival, warehouse staff scan and log the package into the system, linking it to your account via the order number.',
            },
            {
              step: 'Unpacking & Inspection',
              desc: 'Staff carefully open the seller packaging and inspect the item against the order details. They check for correct size, color, visible defects, and overall condition. This is your first line of quality defense.',
            },
            {
              step: 'QC Photography',
              desc: 'Trained photographers take multiple high-resolution photos of the item from various angles. For clothing, this typically includes front, back, side, logo close-ups, tags, and material texture shots. Shoes receive sole, insole, and box condition photos.',
            },
            {
              step: 'System Update',
              desc: 'The QC photos are uploaded to your Sugargoo account, and the item status changes to "Arrived at Warehouse" or "QC Complete." You receive a notification to review the photos and decide on next steps.',
            },
            {
              step: 'Buyers Decision Point',
              desc: 'You review the QC photos in your account dashboard. At this stage, you can approve the item for shipping, request a return/exchange, or leave it in storage while you wait for other items to arrive.',
            },
            {
              step: 'Storage or Forwarding',
              desc: 'Approved items are moved to storage until you submit a shipping request. Returned items are processed back to the seller according to marketplace return policies.',
            },
          ].map((item, i) => (
            <div key={item.step} className="flex gap-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-semibold shrink-0">
                {i + 1}
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground-800 mb-1">{item.step}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Understanding QC Photos: Your Quality Control Safety Net
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          <strong>QC (Quality Control) photos</strong> are the single most valuable feature of the warehouse process. These detailed photographs give you a clear view of what you actually received — not what the seller listing promised — before the item ever leaves China.
        </p>
        <div className="space-y-3 mb-4">
          {[
            {
              aspect: 'What QC Photos Show',
              detail: 'Detailed views of the actual product including stitching quality, material texture, color accuracy, logo placement, tags, labels, and packaging condition.',
            },
            {
              aspect: 'What They Cannot Show',
              detail: 'Fit and sizing on your body, long-term durability, exact color under your lighting, and how the material feels to the touch. Use them alongside spreadsheet quality notes for best results.',
            },
            {
              aspect: 'When to Request More',
              detail: 'If the standard QC photos do not show a detail you are concerned about, you can often request additional photos of specific areas for a small fee.',
            },
            {
              aspect: 'Red Flags to Watch For',
              detail: 'Misaligned logos, incorrect colors, loose stitching, wrong sizing labels, damaged packaging, or items that clearly differ from the listing description.',
            },
          ].map((item) => (
            <div key={item.aspect} className="flex gap-3 p-3 bg-background-100/60 rounded-xl border border-background-200/60">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0 mt-0.5">
                <i className="ri-camera-line w-4 h-4 flex items-center justify-center"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-800 mb-0.5">{item.aspect}</h4>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.detail}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          The QC process is your opportunity to catch problems <strong>before</strong> the item is shipped internationally. Once an item leaves the warehouse for overseas delivery, returning it becomes exponentially more difficult and expensive.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Warehouse Storage Rules and Timeframes
        </h2>
        <div className="bg-background-50 rounded-xl border border-background-200/60 overflow-hidden mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200/60 bg-background-100/50">
                <th className="text-left px-4 py-3 font-semibold text-foreground-800">Storage Aspect</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-800">Details</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Free Storage Period', 'Typically 90 to 180 days depending on current policies'],
                ['Extended Storage', 'Available for a daily fee after the free period expires'],
                ['Abandoned Items', 'Items left beyond maximum storage may be discarded or donated'],
                ['Consolidation Window', 'You can wait for multiple items to arrive before shipping together'],
                ['Item Limits', 'Generally no hard limit on number of items stored simultaneously'],
                ['Access', 'View and manage all stored items through your online dashboard'],
              ].map((row) => (
                <tr key={row[0]} className="border-b border-background-200/40 last:border-0">
                  <td className="px-4 py-2.5 font-medium text-foreground-800">{row[0]}</td>
                  <td className="px-4 py-2.5 text-foreground-600">{row[1]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          The free storage period is one of Sugargoo most buyer-friendly features. It allows you to place multiple orders over weeks or even months, then consolidate everything into one cost-efficient international shipment. Just keep track of your storage timeline to avoid unexpected fees.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Package Consolidation: The Ultimate Cost Saver
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          <strong>Package consolidation</strong> is the process of combining multiple individual items into a single shipping package. This is where the warehouse truly shines as a money-saving tool.
        </p>
        <div className="space-y-3 mb-4">
          {[
            'Shipping one 1kg package and another 1kg package separately might cost $40 each',
            'Shipping the same 2kg combined in one package typically costs $55-60 total',
            'That is a savings of $20-25 just from consolidation — before any coupons or discounts',
            'The savings scale with more items: 5 items consolidated often cost 40-50% less per item than shipping individually',
          ].map((point, i) => (
            <div key={i} className="flex gap-3">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 shrink-0 mt-0.5">
                <i className="ri-check-line w-3 h-3 flex items-center justify-center"></i>
              </div>
              <span className="text-sm text-foreground-700">{point}</span>
            </div>
          ))}
        </div>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          When consolidating, you can also request value-added services:
        </p>
        <ul className="space-y-2 mb-4">
          {[
            'Remove shoe boxes, tags, and extra packaging to reduce weight',
            'Add waterproof packaging for protection during transit',
            'Request corner reinforcement for fragile items',
            'Apply stretch wrap for package integrity',
            'Include invoice or gift note if needed for customs',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <i className="ri-checkbox-circle-line w-4 h-4 flex items-center justify-center text-primary-500 mt-0.5 shrink-0"></i>
              <span className="text-sm text-foreground-700">{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          Always request package removal for fashion items unless you specifically want to keep boxes. The weight savings almost always justify any small service fee.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Returns, Exchanges, and Problem Resolution
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          Not every order goes perfectly. Sometimes items arrive at the warehouse with defects, wrong sizing, or quality issues. Here is how to handle these situations:
        </p>
        <div className="space-y-4">
          {[
            {
              title: 'Request a Return',
              desc: 'If QC photos reveal a significant issue, you can request a return to the seller within the return window (usually 7 days from warehouse arrival). Sugargoo staff will coordinate the return and refund process.',
            },
            {
              title: 'Request an Exchange',
              desc: 'For wrong sizes or colors, exchanges depend on the seller policy. Some sellers accept exchanges; others only offer refunds. Sugargoo will attempt the exchange but cannot guarantee seller cooperation.',
            },
            {
              title: 'Report Missing Items',
              desc: 'If an order arrives incomplete, document the issue immediately through the platform. Sugargoo will investigate with the seller and courier to determine responsibility.',
            },
            {
              title: 'Minor Defects Decision',
              desc: 'For small, acceptable flaws, you must decide whether to keep or return. Consider the cost of return shipping versus the severity of the defect. Sometimes it is more practical to accept minor issues.',
            },
          ].map((item) => (
            <div key={item.title} className="flex gap-3 p-4 bg-background-100/60 rounded-xl border border-background-200/60">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 shrink-0 mt-0.5">
                <i className="ri-arrow-go-back-line w-4 h-4 flex items-center justify-center"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-800 mb-1">{item.title}</h4>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Conclusion: Master the Warehouse, Master Your Savings
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          The <strong>Sugargoo warehouse</strong> is far more than just a storage facility — it is a quality control checkpoint, a consolidation hub, and your primary tool for managing international shipping costs. Understanding how to use the warehouse effectively separates casual shoppers from cost-efficient buyers.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          Your key takeaways should be: always review QC photos carefully, consolidate shipments whenever possible, remove unnecessary packaging, and stay aware of storage timeframes. Combined with smart product discovery through a <strong>Sugargoo Spreadsheet</strong>, these warehouse strategies can cut your total order costs by a third or more.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          The warehouse is your ally in the international shopping process. Use it wisely, and every order becomes smoother, safer, and more affordable.
        </p>
      </section>
    </ArticleLayout>
  );
}